/**
 * Snippet API functions for wizard-based media capture
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// =============================================================================
// Types
// =============================================================================

export interface SnippetDraftData {
  // Step 1: Image preparation
  rotation: number | null;
  crop_applied: boolean;
  enhancements: {
    contrast: number | null;
    brightness: number | null;
    sharpness: number | null;
    grayscale: boolean;
    auto_orient: boolean;
  } | null;

  // Step 2: Classification
  snippet_type: "image" | "document" | null;
  image_subtype: "person" | "gravestone" | "place" | "other" | null;  // For images
  text_type: "printed" | "handwritten" | "combination" | "unknown" | null;  // For documents
  record_subtype: string | null;  // For documents

  // Step 3: Metadata
  caption: string | null;
  description: string | null;
  estimated_date_text: string | null;
  location: string | null;
  location_id: number | null;  // Linked location entity
  memory_id: number | null;    // Linked memory entity
  person_ids: number[];        // Tagged family tree persons
  connected_person_ids: number[];  // Tagged connected persons

  // Wizard state
  current_step: number;
  completed_steps: number[];
}

export interface SnippetItem {
  id: number;
  filename: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  is_snippet: boolean;
  snippet_status: "draft" | "finalized" | null;
  snippet_type: "image" | "document" | null;
  image_subtype: "person" | "gravestone" | "place" | "other" | null;
  text_type: "printed" | "handwritten" | "combination" | "unknown" | null;
  record_subtype: string | null;
  caption: string | null;
  description: string | null;
  estimated_date_text: string | null;
  location: string | null;
  transcription: string | null;  // OCR extracted text
  draft_data: SnippetDraftData | null;
  created_at: string;
}

export interface RecordSubtype {
  value: string;
  label: string;
}

export interface ImageInfo {
  width: number;
  height: number;
  format: string | null;
  mode: string;
  has_exif_orientation: boolean;
}

export interface EnhancementOptions {
  contrast?: number;
  brightness?: number;
  sharpness?: number;
  grayscale?: boolean;
  auto_orient?: boolean;
}

export interface FinalizeData {
  snippet_type: "image" | "document";
  image_subtype?: "person" | "gravestone" | "place" | "other";  // For images
  text_type?: "printed" | "handwritten" | "combination" | "unknown";  // For documents
  record_subtype?: string;  // For documents
  caption?: string;
  description?: string;
  estimated_date_text?: string;
  estimated_date?: string;
  location?: string;
  person_ids?: number[];          // Tagged family tree persons
  connected_person_ids?: number[];  // Tagged connected persons
  tree_id?: number;
}

// =============================================================================
// Constants
// =============================================================================

export const SNIPPET_TYPES = [
  { value: "image", label: "Image", description: "Photograph, illustration, or other visual" },
  { value: "document", label: "Document", description: "Text-based record or certificate" },
] as const;

export const TEXT_TYPES = [
  { value: "printed", label: "Printed", description: "Machine-printed text" },
  { value: "handwritten", label: "Handwritten", description: "Handwritten text" },
  { value: "combination", label: "Combination", description: "Mix of printed and handwritten" },
  { value: "unknown", label: "Unknown", description: "Not sure or not applicable" },
] as const;

export const IMAGE_SUBTYPES = [
  { value: "person", label: "Person / Portrait", description: "Photo of a person or group" },
  { value: "gravestone", label: "Gravestone / Memorial", description: "Headstone, memorial, or burial marker" },
  { value: "place", label: "Place / Building", description: "Location, house, church, etc." },
  { value: "other", label: "Other Image", description: "Other type of photograph" },
] as const;

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get the URL for snippet media
 */
export function getSnippetMediaUrl(snippetId: number): string {
  // Use the standard media endpoint since snippets are stored as Media
  return `${API_URL}/media/${snippetId}`;
}

/**
 * Get available record subtypes for classification
 */
export async function getRecordSubtypes(token: string): Promise<RecordSubtype[]> {
  const response = await fetch(`${API_URL}/snippets/record-subtypes`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch record subtypes");
  }

  return response.json();
}

/**
 * Upload an image to create a new snippet draft
 */
export async function uploadSnippet(
  file: File | Blob,
  token: string,
  treeId?: number
): Promise<SnippetItem> {
  const formData = new FormData();
  
  // Handle Blob vs File
  if (file instanceof File) {
    formData.append("file", file, file.name);
  } else {
    // Clipboard paste creates a Blob, need to give it a name
    const ext = file.type.includes("png") ? ".png" : ".jpg";
    formData.append("file", file, `clipboard_paste${ext}`);
  }

  if (treeId) {
    formData.append("tree_id", String(treeId));
  }

  const response = await fetch(`${API_URL}/snippets/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Upload failed" }));
    throw new Error(error.detail || "Failed to upload snippet");
  }

  return response.json();
}

/**
 * Get a snippet by ID
 */
export async function getSnippet(snippetId: number, token: string): Promise<SnippetItem> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch snippet");
  }

  return response.json();
}

/**
 * List all draft snippets
 */
export async function listDraftSnippets(
  token: string,
  treeId?: number
): Promise<SnippetItem[]> {
  const params = new URLSearchParams();
  if (treeId) {
    params.append("tree_id", String(treeId));
  }

  const url = `${API_URL}/snippets/${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch draft snippets");
  }

  return response.json();
}

/**
 * Update snippet draft data (auto-save)
 */
export async function updateSnippetDraft(
  snippetId: number,
  draftData: Partial<SnippetDraftData>,
  token: string
): Promise<SnippetItem> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}/draft`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ draft_data: draftData }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Update failed" }));
    throw new Error(error.detail || "Failed to update snippet draft");
  }

  return response.json();
}

/**
 * Finalize a snippet and move it to the media library
 */
export async function finalizeSnippet(
  snippetId: number,
  data: FinalizeData,
  token: string
): Promise<SnippetItem> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}/finalize`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Finalization failed" }));
    throw new Error(error.detail || "Failed to finalize snippet");
  }

  return response.json();
}

/**
 * Delete a draft snippet
 */
export async function deleteSnippet(snippetId: number, token: string): Promise<void> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Delete failed" }));
    throw new Error(error.detail || "Failed to delete snippet");
  }
}

/**
 * Get image information for a snippet
 */
export async function getSnippetImageInfo(
  snippetId: number,
  token: string
): Promise<ImageInfo> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}/image-info`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to get image info");
  }

  return response.json();
}

/**
 * Apply image enhancements to a snippet
 */
export async function enhanceSnippetImage(
  snippetId: number,
  options: EnhancementOptions,
  token: string
): Promise<SnippetItem> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}/enhance`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Enhancement failed" }));
    throw new Error(error.detail || "Failed to enhance image");
  }

  return response.json();
}

/**
 * Run OCR on a snippet to extract text
 */
export async function runSnippetOCR(
  snippetId: number,
  token: string
): Promise<SnippetItem> {
  const response = await fetch(`${API_URL}/snippets/${snippetId}/ocr`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "OCR failed" }));
    throw new Error(error.detail || "Failed to extract text");
  }

  return response.json();
}

/**
 * Rotate a snippet image (uses the existing media rotation endpoint)
 */
export async function rotateSnippet(
  snippetId: number,
  degrees: 90 | 180 | 270,
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}/media/${snippetId}/rotate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ degrees }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Rotation failed" }));
    throw new Error(error.detail || "Failed to rotate image");
  }
}

/**
 * Crop a snippet image (uses the existing media crop endpoint)
 */
export async function cropSnippet(
  snippetId: number,
  crop: { x: number; y: number; width: number; height: number },
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}/media/${snippetId}/crop`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(crop),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Crop failed" }));
    throw new Error(error.detail || "Failed to crop image");
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Create initial draft data for a new snippet
 */
export function createInitialDraftData(): SnippetDraftData {
  return {
    rotation: null,
    crop_applied: false,
    enhancements: null,
    snippet_type: null,
    image_subtype: null,
    text_type: null,
    record_subtype: null,
    caption: null,
    description: null,
    estimated_date_text: null,
    location: null,
    location_id: null,
    memory_id: null,
    person_ids: [],
    connected_person_ids: [],
    current_step: 1,
    completed_steps: [],
  };
}

/**
 * Check if an image file type is valid for snippets
 */
export function isValidSnippetImageType(file: File | Blob): boolean {
  const validTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/tiff",
    "image/bmp",
  ];
  return validTypes.includes(file.type);
}

/**
 * Get a display label for snippet type
 */
export function getSnippetTypeLabel(type: string | null): string {
  const found = SNIPPET_TYPES.find((t) => t.value === type);
  return found?.label || "Unknown";
}

/**
 * Get a display label for text type
 */
export function getTextTypeLabel(type: string | null): string {
  const found = TEXT_TYPES.find((t) => t.value === type);
  return found?.label || "Unknown";
}

/**
 * Get a display label for image subtype
 */
export function getImageSubtypeLabel(type: string | null): string {
  const found = IMAGE_SUBTYPES.find((t) => t.value === type);
  return found?.label || "Unknown";
}
